import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // Warnify Pro Warnings opens its preorder popup on mousedown/touchstart (not click) and, after OK, adds its
    // line property and clicks the button again with jQuery data "els-ignore". A real click therefore used to
    // submit once immediately (no property) and again after OK (with property): two lines from one confirmation.
    // Note when Warnify claims a press (it calls preventDefault); window bubble runs after its body handler.
    const noteWarnify = (e) => {
      if (e.defaultPrevented && e.target instanceof Element && this.contains(e.target) && e.target.closest('button[name="add"]')) {
        this.#warnifyPendingUntil = Date.now() + 1500;
      }
    };
    window.addEventListener('mousedown', noteWarnify, opts);
    window.addEventListener('touchstart', noteWarnify, { signal: this.#ac.signal, passive: true });

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      const warnify = this.#warnifyGate(event.submitter);
      if (warnify === 'wait') return;
      if (warnify === 'loading') {
        this.#retryWhenWarnifyReady(event.target, event.submitter);
        return;
      }

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #warnifyPendingUntil = 0;

  /** Warnify's embed is on this page (it prints window.Elspw). */
  #warnifyOnPage() {
    return typeof window.Elspw === 'object' && window.Elspw !== null;
  }

  /**
   * Whether one of Warnify's own rules covers this product, read from the rule list and product data the Warnify
   * embed prints into the page (window.Elspw.config.alerts, window.Elspw.params.product). Mirrors Warnify's
   * product matching: "all", collection overlap, or the rule tag (tag/variant rules); "Els PW ignore" opts out.
   * Anything this can't evaluate (metafield targeting, unknown rule types, missing data) counts as covered, so a
   * covered product never skips the warning; only clearly uncovered products avoid waiting on Warnify.
   */
  #warnifyCoversProduct() {
    const config = window.Elspw?.config;
    const product = window.Elspw?.params?.product;
    if (!config || config.enabled === false) return false;
    const alerts = Array.isArray(config.alerts) ? config.alerts.filter((alert) => alert && Number(alert.enabled) === 1) : null;
    if (!alerts) return true;
    if (!alerts.length) return false;
    if (!product || config.targets_in_metafield) return true;

    const tags = Array.isArray(product.tags) ? product.tags.map(String) : [];
    if (tags.includes('Els PW ignore')) return false;
    const collectionIds = (product.collection_ids || []).map(Number);

    return alerts.some((alert) => {
      switch (alert.based_on) {
        case 'all':
          return true;
        case 'collections':
          return (alert.collection_ids || []).some((id) => collectionIds.includes(Number(id)));
        case 'variants':
          return Boolean(alert.tag) && tags.some((tag) => tag.includes(alert.tag));
        case 'tags':
        case 'products':
          return Boolean(alert.tag) && tags.includes(alert.tag);
        default:
          return true;
      }
    });
  }

  /** Warnify has loaded its jQuery and bound its add-to-cart handler. */
  #warnifyReady() {
    const $ = window.ElspwjQuery;
    const events = $?._data?.(document.body, 'events');
    return Boolean(events && (events.mousedown?.length || events.touchstart?.length));
  }

  /**
   * 'go' to add now, 'wait' when Warnify is showing (or about to show) its popup, 'loading' when Warnify is on the
   * page but not ready for a product its rules cover. Coverage comes from Warnify's own printed rules (no hardcoding).
   */
  #warnifyGate(submitter) {
    if (!this.#warnifyOnPage() || !(submitter instanceof HTMLElement)) return 'go';
    const $ = window.ElspwjQuery;

    // Warnify's own confirmed (or no-warning-needed) click: add once, then reset so the next add asks again.
    if ($ && $(submitter).data('els-ignore')) {
      $(submitter).removeData('els-ignore');
      this.#warnifyPendingUntil = 0;
      return 'go';
    }

    // The click that follows a mousedown Warnify already claimed: its popup is open, so don't add yet.
    if (Date.now() < this.#warnifyPendingUntil) return 'wait';

    // Products no Warnify rule covers add right away and never wait on (or get blocked by) Warnify loading.
    if (!this.#warnifyCoversProduct()) return 'go';

    if (!this.#warnifyReady()) return 'loading';

    // Keyboard, sticky bar or other clicks without a mousedown: hand the press to Warnify the way it listens.
    const press = new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window });
    submitter.dispatchEvent(press);
    return press.defaultPrevented ? 'wait' : 'go';
  }

  #retryWhenWarnifyReady(form, submitter) {
    if (this.#warnifyWaiting) return;
    this.#warnifyWaiting = true;
    this.#setSubmitting(true, submitter);
    const started = Date.now();
    const tick = () => {
      if (!this.isConnected) return;
      if (this.#warnifyReady()) {
        this.#warnifyWaiting = false;
        this.#setSubmitting(false);
        const decision = this.#warnifyGate(submitter);
        if (decision === 'go') this.#submit(form, submitter);
        return;
      }
      if (Date.now() - started > 8000) {
        // Only products a Warnify rule covers reach this wait. Never add those around Warnify: ask to retry.
        this.#warnifyWaiting = false;
        this.#setSubmitting(false);
        this.#showError(this.querySelector('[data-add-to-cart-error]')?.dataset.warnifyUnavailable || 'Please refresh the page and try again.');
        return;
      }
      setTimeout(tick, 150);
    };
    tick();
  }

  #warnifyWaiting = false;

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  /*
   * Adds through Shopify's /cart/add.js (as Shark and Shopify's reference themes do), not the Storefront API
   * cartLinesAdd that Shopify.actions.updateCart uses. STOQ attaches its preorder line properties
   * (_stoq_shopify_selling_plan_id, Purchase type, Shipping) by intercepting /cart/add requests only. Through
   * cartLinesAdd a line arrived without them and STOQ patched it afterwards by line position, so a repeat add
   * never matched the existing line and Shopify kept them as separate lines. Now every add carries the same
   * STOQ + Warnify properties when it is created, so identical adds merge into one line.
   */
  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      const item = { id: Number(line.merchandiseId), quantity: line.quantity };
      if (line.sellingPlanId) item.selling_plan = Number(line.sellingPlanId);
      if (line.attributes?.length) {
        item.properties = Object.fromEntries(line.attributes.map(({ key, value }) => [key, value]));
      }

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
        signal,
      });

      this.#throwIfAborted(signal);

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        this.#showError(data?.description || data?.message || this.dataset.errorMessage || '');
        return;
      }

      // Redraw every mounted cart (drawer, count, cart page), then let Navidium re-read the cart.
      try {
        await customElements.get('s-cart')?.refreshMountedCarts?.({ signal });
      } catch (refreshError) {
        if (signal.aborted) return;
      }
      window.dispatchEvent(new CustomEvent('nvd_cart_changed', { detail: { source: 'theme' } }));
      document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true, detail: { ...this.#eventDetail(), item } }));

      await window.Shopify?.actions?.openCart?.();
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(error?.message && error.name === 'CartRequestCancelledError' ? error.message : (getCartErrorMessage(error) || this.dataset.errorMessage || ''));
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    // One attribute per key (last value wins). Apps such as Warnify append a hidden properties[...] input on
    // every confirmation, so repeated confirmations on one page would otherwise send the same key twice.
    const byKey = new Map();

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      byKey.set(match[1], attributeValue);
    }

    return [...byKey].map(([key, value]) => ({ key, value }));
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
